import { useEffect, useState } from 'react';
import { useConfiguration } from '../api/configuration';
import { registerDynamicFederation } from '../components/wealth/balanceEntryModal/clm/federation.util';
import {
    WEALTH_ASSET_BANKABLE_CATEGORY_ID,
    WEALTH_ASSET_INSURANCE_CATEGORY_ID,
    WEALTH_ASSET_OTHER_CATEGORY_ID,
    WEALTH_ASSET_PENSION_CATEGORY_ID,
    WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID,
    WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID,
    WEALTH_EXPENSE_GENERAL_LIVING,
    WEALTH_EXPENSE_INTEREST_BURDEN,
    WEALTH_EXPENSE_OTHER,
    WEALTH_EXPENSE_PREMIUMS,
    WEALTH_EXPENSE_REAL_ESTATE,
    WEALTH_INCOME_EMPLOYMENT,
    WEALTH_INCOME_INSURANCE,
    WEALTH_INCOME_OTHER,
    WEALTH_INCOME_PENSION,
    WEALTH_INCOME_REAL_ESTATE,
    WEALTH_INCOME_SECURITIES,
    WEALTH_INCOME_SHAREHOLDING,
    WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID,
    WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID,
    WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID,
} from '../config/constants';
import type { IMap } from '../interfaces/generic';
import { useLoadingElement } from './useLoadingElement';

const categoryModuleMap: IMap<string> = {
    // assets
    [WEALTH_ASSET_BANKABLE_CATEGORY_ID]: 'wo-bankable-asset',
    [WEALTH_ASSET_PENSION_CATEGORY_ID]: 'wo-pension-asset',
    [WEALTH_ASSET_INSURANCE_CATEGORY_ID]: 'wo-insurance-asset',
    [WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID]: 'wo-real-estate-asset',
    [WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID]: 'wo-shareholding-asset',
    [WEALTH_ASSET_OTHER_CATEGORY_ID]: 'wo-other-asset',
    // liabilities
    [WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID]: 'wo-liability-modal',
    [WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID]: 'wo-liability-modal',
    [WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID]: 'wo-liability-modal',
    [WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID]: 'wo-liability-modal',
    [WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID]: 'wo-liability-modal',
    [WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID]: 'wo-liability-modal',
    // expenses
    [WEALTH_EXPENSE_PREMIUMS]: 'wo-expense-modal',
    [WEALTH_EXPENSE_REAL_ESTATE]: 'wo-expense-modal',
    [WEALTH_EXPENSE_GENERAL_LIVING]: 'wo-expense-modal',
    [WEALTH_EXPENSE_INTEREST_BURDEN]: 'wo-expense-modal',
    [WEALTH_EXPENSE_OTHER]: 'wo-expense-modal',
    // income
    [WEALTH_INCOME_EMPLOYMENT]: 'wo-income-modal',
    [WEALTH_INCOME_OTHER]: 'wo-income-modal',
    [WEALTH_INCOME_SECURITIES]: 'wo-income-modal',
    [WEALTH_INCOME_REAL_ESTATE]: 'wo-income-modal',
    [WEALTH_INCOME_PENSION]: 'wo-income-modal',
    [WEALTH_INCOME_INSURANCE]: 'wo-income-modal',
    [WEALTH_INCOME_SHAREHOLDING]: 'wo-income-modal',
};

/**
 * Load CLM wealth overview web components
 *
 * @param wealthOverviewCategoryId
 */
export default function useFederatedModule(wealthOverviewCategoryId: number) {
    const moduleName = categoryModuleMap[wealthOverviewCategoryId];
    const [status, setStatus] = useState<FederatedModuleStatus>('loading');
    const isLoading = useLoadingElement(moduleName);
    const { data: configuration } = useConfiguration();

    useEffect(() => {
        (async function () {
            try {
                if (configuration && !customElements.get(moduleName)) {
                    registerDynamicFederation('wo', `./${moduleName}`, `${configuration.clmMicroFrontendHost}/remoteEntry.js`, 'esm', 'webpack');
                }
                if (!isLoading) {
                    setStatus('done');
                }
            } catch (e) {
                console.error(e);
                setStatus('error');
            }
        })();
    }, [wealthOverviewCategoryId, isLoading, moduleName, configuration]);

    return { status };
}

type FederatedModuleStatus = 'loading' | 'done' | 'error';
